/**
 * @description: 通用的AJAX请求封装，
 * @return 使用方式 import Request, { $request, $get, $post, $put, $delete } from '@/utils/request';
 */
import { ElMessage } from 'element-plus';
import axios, {
  AxiosInstance, AxiosRequestConfig, AxiosResponse, Canceler, AxiosError
} from 'axios';
import { LocalStorageService } from '@/utils/storage';
import envData from './env';
import proxyMsg from '../../dev.config';

const { baseApi, apiKey } = envData;
const sourceAjaxList: Canceler[] = [];

/**
 * 取消请求的机制（用于取消重复请求接口）
 * */
const pendingQueue = new Map()
const CancelToken = axios.CancelToken
// 判断请求是否在队列中，如果在就对队列中的该请求执行取消操作
const judgePendingFunc = function (config: AxiosRequestConfig<any>) {
  if (pendingQueue.has(`${config.method}->${config.url}`)) {
    pendingQueue.get(`${config.method}->${config.url}`)() //注意这里的括号不要漏掉
  }
}
// 删除队列中对应已执行的请求
const removeResolvedFunc = function (config: AxiosRequestConfig<any>) {
  if (pendingQueue.has(`${config.method}->${config.url}`)) {
    pendingQueue.delete(`${config.method}->${config.url}`)
  }
}

const getUrlQuery = function () {
  let url = window.location.search
  const obj:any = {}
  if (url.includes('?')) {
    url = url.replace(/\?/g, '')
    if (url.includes('&')) {
      const arr = url.split('&')
      arr.forEach((item) => {
        let privateArr = []
        privateArr = item.split('=')
        obj[privateArr[0]] = privateArr[1]
      })
    } else {
      const arr = url.split('=')
      obj[arr[0]] = arr[1]
    }
  }
  return obj
};

type Result<T> = {
  code: number;
  message: string;
  result?: T;
};
const headers:any = {
  'X-Gaia-Api-Key': import.meta.env.MODE === 'development' ? proxyMsg.API_KEY : apiKey,
}
if (getUrlQuery().CASTGC) {
  headers.token = getUrlQuery().CASTGC
}
/**
 * @description: 需要主动调用new Request(), 可传递更多配置参数进去
 * @return {*}
 */
export default class Request {
  instance: AxiosInstance;

  baseConfig: AxiosRequestConfig = {
    headers: headers,
    baseURL: import.meta.env.MODE === 'development' ? '' : baseApi,
    // timeout: 30000,
    withCredentials: true,
  };

  constructor(axiosConfig?: AxiosRequestConfig) {
    this.instance = axios.create(Object.assign(this.baseConfig, axiosConfig));

    this.instance.interceptors.request.use(
      (requestConfig: AxiosRequestConfig) => {
        if (requestConfig.CancelToken) {
          judgePendingFunc(requestConfig) // 请求发起之前先调用judgePendingFunc方法
        }
        // 将pending队列中的请求设置为当前
        requestConfig.cancelToken = new CancelToken((cb) => {
          // 设置 cancel token  用于取消请求 (当一个接口出现401后，取消后续多有发起的请求，避免出现好几个错误提示)
          sourceAjaxList.push(cb);
          if (requestConfig.CancelToken) {
            pendingQueue.set(`${requestConfig.method}->${requestConfig.url}`, cb) // cb就是取消该请求的方法，调用它就能cancel掉当前请求
          }
        })

        // 这里一个token，你需要在这里取到你设置好的token的值 示例逻辑代码
        const token = LocalStorageService.get('_user_info')?.sessionId || '';
        if (token) {
          // 这里将token设置到headers中，header的key是Authorization，这个key值根据你的需要进行修改即可
          requestConfig.headers = {
            SESSIONID: token,
            ...requestConfig.headers,
          };
        }
        return requestConfig;
      },
      (error: any) => Promise.reject(error)
    );

    this.instance.interceptors.response.use(
      (response: AxiosResponse) => {
        if (response.config.CancelToken) {
          removeResolvedFunc(response.config) // 调用removeResolvedFunc在队列中删除执行过的请求
        }
        // 由外部进行错误处理
        if (response.config.handledError) {
          return response.data;
        }
        const requestUrl = response.config.url || '';
        const result = response.data;

        // 返回数据的code 不为200即为数据出错
        if (+result.code === 200) {
          return result;
        }

        if (result.code === 401) {
          window.location.href = result.loginUrl || '';
          throw new Error(`${result.loginUrl} 需要重新登录`);
        }

        if (result.code === 403) { // 接口未授权
          ElMessage.error(`${requestUrl} 暂无访问权限`);
          throw new Error(`${requestUrl} 暂无访问权限`);
        }

        ElMessage.error(result.message || result.msg || 'Error');
        return result;
        // throw new Error(result.message || result.msg || 'Error'); // 预期结果失败
      },
      (err: AxiosError) => {
        // 由外部进行错误处理
        if (err.config.handledError) {
          return;
        }

        const requestUrl = err.config.url || '';
        if (err.toString().includes('timeout')) {
          ElMessage.error('请求超时，请稍后再试');
        }
        if (err && err.response) {
          console.log('err----',err)
          const { status, config }: any = err.response;

          const isProd = window.location.host.indexOf('idata.longfor.com') >= 0;
          const loginHost = isProd
            ? 'https://sso.longfor.com/cas/new/login'
            : 'https://sso-uat.longfor.com/cas/login';
          const loginQuery = `service=${encodeURIComponent(window.location.origin)}&sysCode=XM2242301`;

          switch (status) {
            case 400:
              err.message = '请求错误';
              break;

            case 401:
              // 本地调试提交数据
              if (window.location.hostname === 'localhost'){
                const a = 1
                if (a > 0) break
              }
              // location.href = data.loginUrl;
              window.location.href = `${loginHost}?${loginQuery}`;
              // 出现未登录或登录失效取消后面的请求
              if (sourceAjaxList.length > 0) {
                sourceAjaxList.forEach((ajaxCancel, index) => {
                  ajaxCancel(); // 取消请求
                  delete sourceAjaxList[index];
                });
              }
              break;

            case 403:
              err.message = `${requestUrl} 暂无访问权限`;
              break;

            case 404:
              err.message = `请求地址出错: ${config.url}`;
              break;

            case 408:
              err.message = '请求超时';
              break;

            case 500:
            case 501:
            case 502:
            case 503:
            case 504:
              err.message = '服务器开个小差，请稍后再试';
              break;

            case 505:
              err.message = 'HTTP版本不受支持';
              break;
            case 0:
              err.message = '请尝试切换内网后重新访问';
              break;
            default:
              err.message = `status：${status}；请求失败，请稍后再试`;
          }
          ElMessage.error(err.message);
        }
        return Promise.reject(err);
      }
    );
  }

  public request(config: AxiosRequestConfig): Promise<AxiosResponse> {
    return this.instance.request(config);
  }

  public get<T = any>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<Result<T>>> {
    return this.instance.get(url, config);
  }

  public post<T = any>(
    url: string,
    data?: TypeObject,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<Result<T>>> {
    return this.instance.post(url, data, config);
  }

  public put<T = any>(
    url: string,
    data?: TypeObject,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<Result<T>>> {
    return this.instance.put(url, data, config);
  }

  public delete<T = any>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<Result<T>>> {
    return this.instance.delete(url, config);
  }
}

/**
 * @description: 封装默认配置的axios的request、get、post、put、delete
 * @return {*}
 */
const server = new Request();
const $request = (config: AxiosRequestConfig): Promise<AxiosResponse> => server.request(config);

const $get = <T = any>(
  url: string,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<Result<T>>> => server.get(url, config);

const $post = <T = any>(
  url: string,
  data?: TypeObject,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<Result<T>>> => server.post(url, data, config);

const $put = <T = any>(
  url: string,
  data?: TypeObject,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<Result<T>>> => server.put(url, data, config);

const $delete = <T = any>(
  url: string,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<Result<T>>> => server.delete(url, config);

export {
  $request,
  $get,
  $post,
  $put,
  $delete,
};
